import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  chooseSuddenDeathWinner, closeBattle, createBattle, deleteBattle, exportBattleCsv,
  getBattle, getBattleScreen, listBattles, renewJudgeLink, resetBattleEntry,
  revealBattle, setBattleCurrent, startBattle, useNewBattleEntryVersion as refreshBattleEntryVersion,
} from '@/api/battles';
import { BattleBuilder } from './BattleBuilder';
import { BattleResults, entrySubtitle, teamByKey, teamColor } from './BattleResults';

const POLL_MS = 3500;

async function copyText(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const input = document.createElement('textarea');
  input.value = value; input.setAttribute('readonly', ''); input.style.position = 'fixed'; input.style.opacity = '0';
  document.body.appendChild(input); input.select(); document.execCommand('copy'); input.remove();
}

export function JudgeLinksPanel({ battle, links, onNewLink, pending }) {
  const [copied, setCopied] = useState('');
  const byJudge = new Map((links || []).map((link) => [link.judge_id, link]));
  return <section className="battle-judge-links" aria-labelledby="judge-links-title">
    <div className="battle-section-heading"><div><span>Private judge access</span><h3 id="judge-links-title">Judge links</h3></div></div>
    <p>Send each adult their own link. A new link immediately revokes the old one.</p>
    <div>{battle.judges.map((judge) => {
      const link = byJudge.get(judge.id);
      return <article key={judge.id}><strong>{judge.name}</strong>
        {link ? <button onClick={async () => { await copyText(link.link); setCopied(judge.id); }}>Copy link</button> : <span>Link hidden after refresh</span>}
        <button disabled={pending === `link-${judge.id}`} onClick={() => onNewLink(judge)}>{pending === `link-${judge.id}` ? 'Making...' : 'New link'}</button>
        {copied === judge.id && <small role="status">Copied</small>}
      </article>;
    })}</div>
  </section>;
}

export function CompletionGrid({ battle }) {
  return <div className="battle-completion-wrap"><table className="battle-completion">
    <caption>Judge completion</caption>
    <thead><tr><th scope="col">Entry</th>{battle.judges.map((judge) => <th scope="col" key={judge.id}>{judge.name}</th>)}<th scope="col">Status</th></tr></thead>
    <tbody>{battle.entries.map((entry) => {
      const complete = new Set(entry.completed_judge_ids || []);
      const result = battle.results?.entries?.find((item) => item.id === entry.id);
      return <tr key={entry.id} className={entry.id === battle.current_entry_id ? 'current' : ''}>
        <th scope="row">#{entry.position} {entry.title}</th>
        {battle.judges.map((judge) => <td key={judge.id}><span className={`battle-completion-mark ${complete.has(judge.id) ? 'done' : 'missing'}`} aria-label={`${judge.name}: ${complete.has(judge.id) ? 'complete' : 'missing'}`}>{complete.has(judge.id) ? '✓' : '○'}</span></td>)}
        <td>{result?.waiting_on ? `Waiting on ${result.waiting_on} ${result.waiting_on === 1 ? 'judge' : 'judges'}` : 'Complete'}</td>
      </tr>;
    })}</tbody>
  </table></div>;
}

function LiveConsole({ battle, screen, onAction, pending }) {
  const isLive = battle.status === 'live';
  const currentIndex = battle.entries.findIndex((entry) => entry.id === battle.current_entry_id);
  const current = battle.entries[currentIndex] || null;
  const audioChanged = screen?.current?.id === current?.id && screen.current.audio?.changed_after_start;
  const hasScores = Boolean(current?.completed_judge_ids?.length);
  const tied = battle.results?.winner?.tied_team_keys || [];
  const move = (delta) => {
    const next = battle.entries[currentIndex + delta];
    if (next) onAction(`current-${next.id}`, () => setBattleCurrent(battle.id, next.id));
  };
  return <section className="battle-console" aria-labelledby="battle-console-title">
    <div className="battle-section-heading"><div><span>Host controls</span><h3 id="battle-console-title">Live console</h3></div>
      <Link className="battle-projector-link" target="_blank" rel="noreferrer" to={`/facilitator/class/${battle.program_id}/battle/${battle.id}/screen`}>Open projector</Link>
    </div>
    {current && <div className="battle-current-control" aria-live="polite">
      <button disabled={!isLive || currentIndex <= 0 || pending} onClick={() => move(-1)}>Prev</button>
      <div><span>Current entry {current.position} of {battle.entries.length}</span><strong>{entrySubtitle(current)}</strong><small>{teamByKey(battle.teams, current.team_key).name} · {current.selector_name}</small></div>
      <button disabled={!isLive || currentIndex >= battle.entries.length - 1 || pending} onClick={() => move(1)}>Next</button>
    </div>}
    {isLive && audioChanged && <div className="battle-audio-warning" role="alert"><p>This beat changed after the battle started</p>
      {hasScores ? <button disabled={pending} onClick={() => {
        if (window.confirm('Judges will need to score this beat again.')) onAction(`reset-${current.id}`, () => resetBattleEntry(battle.id, current.id));
      }}>Reset this entry</button> : <button disabled={pending} onClick={() => onAction(`refresh-${current.id}`, () => refreshBattleEntryVersion(battle.id, current.id))}>Use the new version</button>}
    </div>}
    {isLive && screen?.current?.audio?.needs_takehome && <p className="battle-audio-warning">Ask the kid to tap Take it home.</p>}
    <CompletionGrid battle={battle} />
    {isLive && <div className="battle-console-actions">
      <button className="primary" disabled={!battle.results?.all_complete || pending} onClick={() => onAction('reveal', () => revealBattle(battle.id, false))}>Reveal</button>
      {!battle.results?.all_complete && <button className="danger" disabled={pending} onClick={() => {
        if (window.confirm('End with partial results? Scores will lock and there will be no winner or sudden death.')) onAction('partial', () => revealBattle(battle.id, true));
      }}>End with partial results</button>}
      <button disabled={pending} onClick={() => exportBattleCsv(battle.id, battle.name)}>Export CSV</button>
    </div>}
    {battle.status === 'revealed' && tied.length >= 2 && !battle.results?.winner?.winner && <fieldset className="battle-sudden-death"><legend>Sudden death winner</legend>
      <p>Choose only from the teams still tied.</p>
      {tied.map((key) => <button key={key} disabled={pending} style={{ '--team-color': teamColor(teamByKey(battle.teams, key).color) }} onClick={() => {
        if (window.confirm(`Choose ${teamByKey(battle.teams, key).name} as the sudden death winner? This cannot be changed.`)) onAction(`sudden-${key}`, () => chooseSuddenDeathWinner(battle.id, key));
      }}>{teamByKey(battle.teams, key).name}</button>)}
    </fieldset>}
  </section>;
}

export function BattlesPanel({ program, seats = [] }) {
  const [battles, setBattles] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [battle, setBattle] = useState(null);
  const [screen, setScreen] = useState(null);
  const [links, setLinks] = useState([]);
  const [building, setBuilding] = useState(false);
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');

  const refreshList = useCallback(async () => {
    try { const rows = await listBattles(program.id); setBattles(Array.isArray(rows) ? rows : []); }
    catch (requestError) { setError(requestError.message); }
  }, [program.id]);
  const refreshBattle = useCallback(async (id = selectedId) => {
    if (!id) return;
    try {
      const [next, nextScreen] = await Promise.all([getBattle(id), getBattleScreen(id)]);
      setBattle(next); setScreen(nextScreen); setError('');
    } catch (requestError) { setError(requestError.message); }
  }, [selectedId]);

  useEffect(() => { refreshList(); }, [refreshList]);
  useEffect(() => { if (selectedId) refreshBattle(selectedId); else { setBattle(null); setScreen(null); } }, [refreshBattle, selectedId]);
  useEffect(() => {
    if (!selectedId || battle?.status !== 'live') return undefined;
    const interval = setInterval(() => refreshBattle(selectedId), POLL_MS);
    return () => clearInterval(interval);
  }, [battle?.status, refreshBattle, selectedId]);

  const action = async (key, request) => {
    setPending(key); setError('');
    try {
      const next = await request();
      if (next?.id) setBattle(next);
      await refreshList();
      if (selectedId) {
        const nextScreen = await getBattleScreen(selectedId).catch(() => null);
        if (nextScreen) setScreen(nextScreen);
      }
      return next;
    } catch (requestError) { setError(requestError.message); return null; }
    finally { setPending(''); }
  };
  const save = async (payload, shouldStart) => {
    const created = await action('create', () => createBattle(program.id, payload));
    if (!created) return;
    setSelectedId(created.id); setBuilding(false);
    if (shouldStart) {
      setPending('start');
      try {
        const started = await startBattle(created.id);
        setBattle(started); setLinks(started.judge_links || []); await refreshList();
      } catch (requestError) { setError(requestError.message); }
      finally { setPending(''); }
    }
  };
  const start = async () => {
    const started = await action('start', () => startBattle(battle.id));
    if (started) setLinks(started.judge_links || []);
  };
  const newLink = async (judge) => {
    const link = await action(`link-${judge.id}`, () => renewJudgeLink(battle.id, judge.id));
    if (link) setLinks((current) => [...current.filter((item) => item.judge_id !== judge.id), link]);
  };
  const canDelete = battle && ['setup', 'closed'].includes(battle.status);
  const statusCounts = useMemo(() => Object.fromEntries(battles.map((item) => [item.status, (battles.filter((row) => row.status === item.status).length)])), [battles]);

  return <section className="battles-panel" aria-labelledby="battles-title">
    <div className="battle-panel-heading"><div><span>Practice and team competitions</span><h2 id="battles-title">Battles</h2></div><button className="primary" onClick={() => { setBuilding(true); setSelectedId(''); }}>New battle</button></div>
    {error && <p className="battle-panel-error" role="alert">{error}</p>}
    {building ? <BattleBuilder seats={seats} namesEnabled={program.names_enabled} busy={pending === 'create' || pending === 'start'} onSave={save} onCancel={() => setBuilding(false)} /> : <>
      <div className="battle-layout">
        <nav className="battle-list" aria-label="Battles">
          {battles.map((item) => <button key={item.id} className={selectedId === item.id ? 'selected' : ''} onClick={() => setSelectedId(item.id)}>
            <strong>{item.name}</strong><span>{item.mode === 'team' ? 'Team battle' : 'Practice'} · {item.status}</span>
          </button>)}
          {!battles.length && <p>No battles yet. Build one when the room is ready.</p>}
        </nav>
        {battle && <div className="battle-detail">
          <header><div><span>{battle.mode === 'team' ? 'Team battle' : 'Practice'} · {battle.status}</span><h3>{battle.name}</h3></div>
            <div>{battle.status === 'setup' && <button className="primary" disabled={pending} onClick={start}>Start battle</button>}
              {battle.status === 'revealed' && <button disabled={pending} onClick={() => action('close', () => closeBattle(battle.id))}>Close</button>}
              {canDelete && <button className="danger" disabled={pending} onClick={async () => {
                if (!window.confirm(`Delete ${battle.name}? This cannot be undone.`)) return;
                const removed = await action('delete', () => deleteBattle(battle.id));
                if (removed !== null) { setSelectedId(''); setBattle(null); await refreshList(); }
              }}>Delete</button>}</div>
          </header>
          {battle.status === 'setup' && <section className="battle-setup-summary"><p>{battle.teams.length} teams · {battle.entries.length} entries · {battle.judges.length} judges</p><p>Starting freezes the original beat versions and creates private judge links.</p></section>}
          {battle.status === 'live' && <JudgeLinksPanel battle={battle} links={links} onNewLink={newLink} pending={pending} />}
          {battle.status === 'live' && <LiveConsole battle={battle} screen={screen} onAction={action} pending={pending} />}
          {['revealed', 'closed'].includes(battle.status) && <>
            <BattleResults results={battle.results} showComments />
            {battle.status === 'revealed' && <LiveConsole battle={battle} screen={screen} onAction={action} pending={pending} />}
            <button onClick={() => exportBattleCsv(battle.id, battle.name)}>Export CSV</button>
          </>}
        </div>}
      </div>
      <span className="battle-status-summary" aria-label="Battle status summary">{Object.entries(statusCounts).map(([status, count]) => `${count} ${status}`).join(' · ')}</span>
    </>}
  </section>;
}

export default BattlesPanel;
